import { StorageService } from './storage.service';
import { Router } from '@angular/router';
import { Observable } from 'rxjs/Observable';
import { Store } from '@ngrx/store';
import { Injectable } from '@angular/core';

import { BrokerService, BrokerSubscriber } from './broker.service';
import { MonitoredHttpState, JWT_STATUS } from './monitored-http.service';
import * as moment from 'moment';

const BACKUP_SOURCE: string = 'ConfigWeb';
const BACKUP_PERSISTENCE: string = 'rolling';

const PROFILE_NAME_PRIMARY_END_USER: string = 'primary';
const STORAGE_KEY_PROFILE: string = 'profile';
const STORAGE_KEY_IS_REMOTE: string = 'isRemote';

export interface BackupEntry {
  id: string;
  name: string;
  time: string;
  size: number;
  url: string;
  build_number: string;
  backup_options: {
    [key: string]: boolean
  }
}

export interface BackupStatus {
  current_operation?: {
    name: string;
    progress: number;
    start_time: number;
  };
  license?: string;
  next_scheduled_backup?: number;
  previous_backup?: {
    end_time: number;
    message: string;
    success: boolean;
  };
  previous_operation?: {
    end_time: number;
    message: string;
    name: string;
    success: boolean;
  };
  error?: any;
}

export interface BackupContext {
  ready: boolean;
  required: boolean;
  checked: boolean;
  complete: boolean;
  running: boolean;
  error: any;
  status?: BackupStatus;
  redirectURL: string;
  list?: Array<BackupEntry>;
}

const INITIAL_STATE: BackupContext = {
  ready: true,
  required: false,
  checked: false,
  complete: false,
  running: false,
  redirectURL: '',
  error: undefined,
  status: {}
};

const STORE_NAME: string = 'C4:BACKUPS:';
const SET_BACKUP_REQUIRED: string = STORE_NAME + 'SET_BACKUP_REQUIRED';
const SET_BACKUP_STATUS: string = STORE_NAME + 'SET_BACKUP_STATUS';
const SET_BACKUP_COMPLETE: string = STORE_NAME + 'SET_BACKUP_COMPLETE';
const SET_REDIRECT_URL: string = STORE_NAME + 'SET_REDIRECT_URL';
const SET_READY: string = STORE_NAME + 'SET_READY';
const SET_RUNNING: string = STORE_NAME + 'SET_RUNNING';
const SET_CHECKED: string = STORE_NAME + 'SET_CHECKED';
const SET_ERROR: string = STORE_NAME + 'SET_ERROR';
const SET_BACKUP_CHECK_RESULT: string = STORE_NAME + 'SET_BACKUP_CHECK_RESULT';

export function backupReducer(state: BackupContext = INITIAL_STATE, { type, payload }) {
  switch (type) {
    case SET_BACKUP_REQUIRED: return Object.assign({}, state, { required: payload });
    case SET_BACKUP_STATUS: return Object.assign({}, state, { status: payload });
    case SET_BACKUP_COMPLETE: return Object.assign({}, state, { complete: payload });
    case SET_REDIRECT_URL: return Object.assign({}, state, { redirectURL: payload });
    case SET_READY: return Object.assign({}, state, { ready: payload });
    case SET_RUNNING: return Object.assign({}, state, { running: payload });
    case SET_CHECKED: return Object.assign({}, state, { checked: payload });
    case SET_ERROR: return Object.assign({}, state, { error: payload });
    case SET_BACKUP_CHECK_RESULT: return Object.assign({}, state, { checked: payload.checked, required: payload.required });

    default: return state;
  }
}

@Injectable()
export class BackupService {

  required: Observable<boolean>;
  checked: Observable<boolean>;
  running: Observable<boolean>;
  previousOperation: Observable<any>;
  currentOperation: Observable<any>;
  lastBackup: Observable<any>;
  error: Observable<any>;

  private _backupSubscriber: BrokerSubscriber<BackupStatus>;

  constructor(
    private store: Store<{
      backup: BackupContext,
      authentication: {
        licenseChecked: boolean
      },
      monitoredHttpState: MonitoredHttpState
    }>,
    private broker: BrokerService,
    private storage: StorageService,
    private router: Router
  ) {
    this.required = this.store.select(s => s.backup.required);
    this.checked = this.store.select(s => s.backup.checked);
    this.running = this.store.select(s => s.backup.running);
    this.previousOperation = this.store.select(s => s.backup.status.previous_operation);
    this.currentOperation = this.store.select(s => s.backup.status.current_operation);
    this.lastBackup = this.store.select(s => s.backup.status.previous_backup);
    this.error = this.store.select(s => s.backup.error);

    Observable.combineLatest(
      this.store.select(s => s.monitoredHttpState.jwtStatus).filter(s => s == JWT_STATUS.VALID),
      this.broker.connected,
      (valid, connected) => valid && connected
    )
      .filter(s => s)
      .take(1)
      .subscribe(s => this.checkForRecentBackup());
  }

  checkForRecentBackup() {
    let profile = this.storage.get(STORAGE_KEY_PROFILE);
    let remote = this.storage.get(STORAGE_KEY_IS_REMOTE);
    console.log('checking for recent backup, profile and remote', profile, remote);
    // NOTE backups are NOT required for system admin or pro-staller
    // NOTE remote access is always by the consumer, which requires backup
    if (profile !== PROFILE_NAME_PRIMARY_END_USER && !remote) {
      this.store.dispatch({ type: SET_BACKUP_CHECK_RESULT, payload: { required: false, checked: true } });
    } else {
      this.broker.call({ path: '/api/v1/agents/backup/list' })
        .then(res => {
          // console.info('backup: backup status response: ', res);
          let backupRequired = true;

          // NOTE might get either an array or object back
          let backups = [];
          if (Array.isArray(res)) {
            backups = res;
          } else if (!!res.backup && Array.isArray(res.backup)) {
            backups = res.backup;
          }

          // console.log('backup: walking backup list looking for recent backup', backups);
          // otherwise resort to examining the list of past backups for one that is recent (< 7 days old)
          let isRecent = false;
          for (let i = 0; i < backups.length; i++) {
            let backup = backups[i];
            // console.log('backup: checking for recent backup ', backup);
            isRecent = (this._isLessThanOneWeekAgo(new Date(Date.parse(backup.time))));
            // console.log('backup: is this recent?', isRecent);
            if (isRecent) {
              backupRequired = false;
              break;
            }
          }

          console.info('setting backup required: ', backupRequired);
          this.store.dispatch({ type: SET_BACKUP_CHECK_RESULT, payload: { required: backupRequired, checked: true } });
          this._ready(true);
        }, err => {
          console.error('backup: error checking backup agent status and list', err);
          this._ready(true);
          this._error(err);
        });
    }
  }

  hasRecentBackup(): boolean {
    let hasRecent = false;
    this.store.select(s => s.backup.required).take(1).subscribe(required => {
      hasRecent = !required;
    });
    return hasRecent;
  }

  private _error(err: any) {
    this.store.dispatch({ type: SET_ERROR, payload: err });
  }

  private _isRecentBackup(backup: any) {
    let date = new Date(Date.parse(backup.time));
    return this._isLessThanOneWeekAgo(date);
  }

  setBackup(dayOfWeek: string): Promise<any> {
    this._ready(false);
    this._error(undefined);
    this.store.dispatch({ type: SET_RUNNING, payload: true });

    return new Promise<any>((resolve, reject) => {
      let data = {
        // backups_enabled: true,
        backup_schedule: {
          days: dayOfWeek || '',
          hour: 0,
          minute: 0,
          second: 0
        },
        backup_options: {
          INCLUDE_PROJECT_DATA: true,
          INCLUDE_PERSONAL_INFO: true,
          INCLUDE_DRIVERS: true,
          INCLUDE_MEDIA: true,
          INCLUDE_COVER_ART: true,
          INCLUDE_SCREEN_SAVER: true,
          INCLUDE_ANNOUNCEMENTS: true,
          INCLUDE_WALLPAPER: true,
          source: BACKUP_SOURCE,
          persistence: BACKUP_PERSISTENCE
        },
        backup_now: true
      };

      this.broker.call({
        path: 'api/v1/agents/backup/setup',
        method: 'POST',
        data: data
      }).then(res => {
        this._subscribeBackupStatus();
        resolve(res);
      }, err => {
        this._error(err);
        this._ready(true);
        reject(err);
      });
    })
  }

  setRedirectURL(url: string) {
    this.store.dispatch({ type: SET_REDIRECT_URL, payload: url });
  }

  private _subscribeBackupStatus() {
    this.broker.connected.subscribe(connected => {
      if (connected) {
        this.broker.subscribe({
          path: '/api/v1/agents/backup'
        }).then(res => {
          this._backupSubscriber = res;
          this._backupSubscriber.subscribe(status => {
            this.handleBackupStatus(status);
          });
        }, err => {
          console.error('backup: error subscribing to current backup status', err);
          // reject(err);
        });
      }
    });
  }

  private _isLessThanOneWeekAgo(then: Date): boolean {
    let m = moment(then);
    // console.info('backup: checking backup on: ', m.toISOString());
    let now = moment();
    // console.info('backup: current date is: ', now.toISOString());
    let oneWeekAgo = now.subtract(1, 'week');
    // console.info('backup: oldest allowed backup date is: ', oneWeekAgo.toISOString());

    let recent = (m.isAfter(oneWeekAgo));
    // console.info('is recent: ', recent);
    return recent;
  }

  private handleBackupStatus(status: BackupStatus) {
    // console.info('backup: handle backup status upate', status);
    this.store.dispatch({ type: SET_BACKUP_STATUS, payload: status });

    if (status.error) {
      this._error(status.error);
      this.store.dispatch({ type: SET_RUNNING, payload: false });
    } else if (!!status.current_operation && status.current_operation.name === 'Idle') {
      // console.info('backup: current backup op is idle, checking last success', status.current_operation);
      if (status.previous_operation.success) {
        // console.info('backup: last operation was success, checking current / previous times', status.previous_operation);
        let date = new Date(status.previous_operation.end_time * 1000); // this is a unix timestamp
        // console.info('backup: parsed date of previous backup end time', date);
        if (this._isLessThanOneWeekAgo(date)) {
          // console.info('backup: current start time and prev end time delta is < 1 week, setting complete', true);
          this.store.dispatch({ type: SET_BACKUP_REQUIRED, payload: false });
          this.store.dispatch({ type: SET_BACKUP_COMPLETE, payload: true });

          if (this._backupSubscriber) {
            this._backupSubscriber.unsubscribe();
            this._backupSubscriber = null;
          }

          this._ready(true);

          this.store.select(s => s.backup.redirectURL).take(1).subscribe(url => {
            console.info('backup: routing to ', url);
            this.router.navigate([url]);
          });
        } else {
          console.warn('backup: backup may have stalled/failed, last result is too old', status);
          this.store.dispatch({ type: SET_BACKUP_COMPLETE, payload: false });
          this._ready(true);
        }
      } else {
        console.error('backup: error creating backup', status);
        this._error({ message: 'An error occurred while preparing or saving this backup. Please contact your Control4 dealer.', context: status });
        this._ready(true);
      }

      this.store.dispatch({ type: SET_RUNNING, payload: false });
    }
  }

  private _ready(state: boolean) {
    this.store.dispatch({ type: SET_READY, payload: state });
  }
}



// WEBPACK FOOTER //
// ./~/@c4/core/src/app/services/backup.service.ts